/**
 * Profile navigation bar containing:
 * - Theme toggle (light/dark)
 * - GitHub link
 * - GitLab link
 * - "Connect" mailto button
 *
 * Compatible with Node 24 / ESM setups.
 */

import { FC } from "react";
import { useTheme } from "next-themes";
import clsx from "clsx";

import { SiGithub } from "@react-icons/all-files/si/SiGithub";
import { SiGitlab } from "@react-icons/all-files/si/SiGitlab";

import { Link } from "components/link";
import DarkmodeIcon from "components/darkmode-icon";

type ProfileNavProps = {
  /** Whether header navigation is currently shown, affecting icon hover colors */
  showNav: boolean;
};

export const ProfileNav: FC<ProfileNavProps> = ({ showNav }) => {
  const { theme, setTheme } = useTheme();

  const iconClasses = clsx(
    "rounded p-2 text-gray-500 transition-colors",
    "d:text-gray-300 d:h:text-gray-50 md:h:text-gray-900",
    showNav ? "h:text-gray-200" : "h:text-gray-900"
  );

  return (
    <nav className="z-10 ml-auto flex gap-1 pl-4">
      {/* Theme toggle */}
      <button
        type="button"
        className={iconClasses}
        onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      >
        <span className="sr-only">Switch Color Theme</span>
        <DarkmodeIcon />
      </button>

      {/* GitHub */}
      <Link
        href="https://github.com/mattummal"
        className={iconClasses}
        target="_blank"
      >
        <span className="sr-only">GitHub</span>
        <SiGithub className="h-5 w-5" />
      </Link>

      {/* GitLab */}
      <Link
        href="https://gitlab.com/mattummal"
        className={iconClasses}
        target="_blank"
      >
        <span className="sr-only">GitLab</span>
        <SiGitlab className="h-5 w-5" />
      </Link>

      {/* Connect CTA */}
      <Link
        target="_blank"
        href="mailto:i@mattumm.al"
        className="button-rainbow ml-4 hidden whitespace-nowrap rounded-none px-4 py-1.5 text-sm font-medium tracking-tight text-gray-500 md:flex"
      >
        Connect
      </Link>
    </nav>
  );
};
